import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, options: Record<string, unknown>) => string;
      reset: (widgetIdOrEl: string | HTMLElement) => void;
      remove: (widgetIdOrEl: string | HTMLElement) => void;
    };
    onTurnstileLoad?: () => void;
  }
}

export interface TurnstileWidgetHandle {
  reset: () => void;
}

interface Props {
  onToken: (token: string) => void;
  className?: string;
}

const SITE_KEY = (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) || '';

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, Props>(({ onToken, className }, ref) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useImperativeHandle(ref, () => ({
    reset: () => {
      onToken('');
      if (window.turnstile && containerRef.current) {
        try { window.turnstile.reset(containerRef.current); } catch { /* ignore */ }
      }
    },
  }), [onToken]);

  useEffect(() => {
    if (!SITE_KEY) return;
    if (document.getElementById('turnstile-script')) {
      if (window.turnstile) setLoaded(true);
      else window.onTurnstileLoad = () => setLoaded(true);
      return;
    }
    const s = document.createElement('script');
    s.id = 'turnstile-script';
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit';
    s.async = true;
    s.defer = true;
    window.onTurnstileLoad = () => setLoaded(true);
    document.head.appendChild(s);
  }, []);

  useEffect(() => {
    if (!SITE_KEY || !loaded || !containerRef.current || widgetIdRef.current) return;
    if (!window.turnstile) return;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: (token: string) => onToken(token),
      'expired-callback': () => onToken(''),
      'error-callback': () => onToken(''),
    });
    return () => {
      if (window.turnstile && containerRef.current) {
        try { window.turnstile.remove(containerRef.current); } catch { /* ignore */ }
      }
      widgetIdRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} className={className} />;
});

TurnstileWidget.displayName = 'TurnstileWidget';
export default TurnstileWidget;
